import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BellRing, ChevronRight, CircleDashed, CheckCircle2, ArrowDownToLine, Inbox, ReceiptText, Stethoscope } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { supabase } from '../../lib/supabase';
import { localDate } from '../../lib/periods';

/**
 * Needs Attention — quiet shortcut card.
 * Rows render only when their count is > 0. When everything is zero we
 * show the "All clear" state rather than hiding the card (a satisfying
 * end-state per the brief, not an empty container).
 */

/** Shape returned by fn_get_unreconciled_parts (20260811160000). */
interface UnreconciledParts {
    status: string;
    job_count: number;
    total: number;
    oldest_days?: number;
    vat_reclaim?: number;
    tax_saving?: number;
}

/**
 * The missing-parts-invoice row has no threshold and no schedule, unlike the
 * notification and email escalation that sits behind the same RPC. It is
 * derived state: it appears the moment a paid job has unrecorded parts and
 * disappears the moment the invoice is uploaded — or the expense is entered by
 * hand, which is the escape hatch when a receipt is genuinely lost. Nothing
 * dismisses it, because "dismissed" and "dealt with" are not the same thing and
 * only one of them stops the user overpaying tax.
 */
const UnreconciledLabel = ({ data }: { data: UnreconciledParts }) => {
    const worth = Number(data.vat_reclaim ?? 0) + Number(data.tax_saving ?? 0);
    return (
        <>
            Parts invoices missing (£{Number(data.total).toFixed(2)})
            {worth > 0 && (
                <span className="block text-xs text-fg-4">
                    Worth about £{worth.toFixed(2)} in VAT and tax
                </span>
            )}
        </>
    );
};
export const NeedsAttentionCard = () => {
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();

    const [overdueInvoices, setOverdueInvoices] = useState<number>(0);
    const [pendingQuotes, setPendingQuotes] = useState<number>(0);
    const [pendingCreditsTotal, setPendingCreditsTotal] = useState<number>(0);
    const [pendingCreditsCount, setPendingCreditsCount] = useState<number>(0);
    const [expensesToReview, setExpensesToReview] = useState<number>(0);
    const [openDiagnostics, setOpenDiagnostics] = useState<number>(0);
    const [unreconciled, setUnreconciled] = useState<UnreconciledParts | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!activeOrgId) return;

        const todayISO = localDate(new Date());

        const fetchAll = async () => {
            const [inv, quo, returns, ocrQueue, creditReview, parts, diagnostics] = await Promise.all([
                supabase
                    .from('invoices')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .is('deleted_at', null)
                    .lt('due_date', todayISO)
                    .not('status', 'in', '(paid,void,refunded)'),
                supabase
                    .from('quotes')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .eq('quote_type', 'original')
                    .in('status', ['draft', 'sent']),
                supabase
                    .from('part_returns')
                    .select('cost_price, quantity')
                    .eq('org_id', activeOrgId)
                    .in('status', ['pending_collection', 'collected', 'credit_matched']),
                // Uploaded expenses the OCR pipeline held back for human review.
                supabase
                    .from('processing_queue')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .in('status', ['needs_review', 'manual_entry']),
                // Supplier-credit matches awaiting confirmation (same Review Queue).
                supabase
                    .from('ocr_review_queue')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .eq('status', 'pending'),
                // Paid jobs whose parts have no supplier invoice behind them.
                // The RPC does the whole calculation (including netting off
                // COGS spend that was uploaded but never linked to a job) so
                // this row can never disagree with the working-capital figure.
                supabase.rpc('fn_get_unreconciled_parts', { p_org_id: activeOrgId }),
                // Diagnostics started and not yet turned into a quote or a job.
                // Without this row the only way back to a half-finished report
                // was Clients → search → the customer → Diagnostics, or knowing
                // to look under a Workshop tab. Both are several taps for
                // something the mechanic put down five minutes ago.
                supabase
                    .from('diagnostic_reports')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .in('status', ['in_progress', 'complete']),
            ]);

            setOverdueInvoices(inv.count ?? 0);
            setPendingQuotes(quo.count ?? 0);
            setExpensesToReview((ocrQueue.count ?? 0) + (creditReview.count ?? 0));
            setOpenDiagnostics(diagnostics.count ?? 0);

            const partsData = parts.data as UnreconciledParts | null;
            setUnreconciled(
                partsData && partsData.status === 'ok' && Number(partsData.job_count) > 0
                    ? partsData
                    : null,
            );

            const rows = (returns.data ?? []) as Array<{ cost_price: number; quantity: number }>;
            const total = rows.reduce((sum, r) => sum + Number(r.cost_price || 0) * Number(r.quantity || 0), 0);
            setPendingCreditsTotal(total);
            setPendingCreditsCount(rows.length);

            setLoading(false);
        };

        fetchAll().catch((err) => {
            console.error('NeedsAttention fetch failed', err);
            setLoading(false);
        });
    }, [activeOrgId]);

    const totalIssues =
        overdueInvoices + pendingQuotes + pendingCreditsCount + expensesToReview +
        openDiagnostics + (unreconciled?.job_count ?? 0);

    return (
        <section className="fd-card flex flex-col h-full" aria-labelledby="dash-attn-title">
            <header className="fd-card-header">
                <h2 id="dash-attn-title" className="fd-card-title">
                    <BellRing aria-hidden="true" />
                    <span className="truncate">Needs attention</span>
                    {!loading && totalIssues > 0 && (
                        <span className="fd-count" aria-label={`${totalIssues} items`}>{totalIssues}</span>
                    )}
                </h2>
            </header>

            {loading ? (
                <div className="px-5 py-4 text-xs text-fg-4 font-mono">—</div>
            ) : totalIssues === 0 ? (
                <div className="flex items-center gap-2.5 px-5 py-5">
                    <CheckCircle2 className="w-5 h-5 text-success" strokeWidth={2} />
                    <span className="text-sm text-fg-2 font-medium">
                        All clear. Nothing needs you right now.
                    </span>
                </div>
            ) : (
                <ul className="fd-card-body flex flex-col">
                    {overdueInvoices > 0 && (
                        <AlertRow
                            icon={<AlertTriangle className="w-4 h-4" />}
                            tone="danger"
                            label="Overdue invoices"
                            count={overdueInvoices}
                            onClick={() => navigate('/invoices?filter=overdue')}
                        />
                    )}
                    {pendingQuotes > 0 && (
                        <AlertRow
                            icon={<CircleDashed className="w-4 h-4" />}
                            tone="amber"
                            label="Quotes awaiting reply"
                            count={pendingQuotes}
                            onClick={() => navigate('/workshop?tab=quotes&filter=pending')}
                        />
                    )}
                    {openDiagnostics > 0 && (
                        <AlertRow
                            icon={<Stethoscope className="w-4 h-4" />}
                            tone="amber"
                            label="Diagnostics to finish"
                            count={openDiagnostics}
                            onClick={() => navigate('/workshop?tab=diagnostics')}
                        />
                    )}
                    {pendingCreditsCount > 0 && (
                        <AlertRow
                            icon={<ArrowDownToLine className="w-4 h-4" />}
                            tone="amber"
                            label={`Supplier credits pending (£${pendingCreditsTotal.toFixed(2)})`}
                            count={pendingCreditsCount}
                            onClick={() => navigate('/returns')}
                        />
                    )}
                    {expensesToReview > 0 && (
                        <AlertRow
                            icon={<Inbox className="w-4 h-4" />}
                            tone="amber"
                            label="Expenses need review"
                            count={expensesToReview}
                            onClick={() => navigate('/expenses?review=1')}
                        />
                    )}
                    {unreconciled && (
                        <AlertRow
                            icon={<ReceiptText className="w-4 h-4" />}
                            // Danger once the oldest gap is a fortnight old: by then
                            // it is not an oversight, it is money being taxed.
                            tone={(unreconciled.oldest_days ?? 0) >= 14 ? 'danger' : 'amber'}
                            label={<UnreconciledLabel data={unreconciled} />}
                            count={unreconciled.job_count}
                            onClick={() => navigate('/expenses')}
                        />
                    )}
                </ul>
            )}
        </section>
    );
};

const AlertRow = ({
    icon,
    tone,
    label,
    count,
    onClick,
}: {
    icon: React.ReactNode;
    tone: 'danger' | 'amber';
    label: React.ReactNode;
    count: number;
    onClick: () => void;
}) => {
    // 'amber' is the historical name for "needs doing, not urgent": it now
    // reads as the ochre warning tone, which is distinct from the brand gold.
    const toneText = tone === 'danger' ? 'text-danger' : 'text-warning';
    return (
        <li className="min-w-0">
            <button onClick={onClick} className="fd-row">
                <span className={`shrink-0 ${toneText}`}>{icon}</span>
                <span className="flex-1 min-w-0 text-sm text-fg-2">{label}</span>
                <span className={`fd-badge ${tone === 'danger' ? 'fd-badge-danger' : 'fd-badge-warning'} tabular-nums`}>
                    {count}
                </span>
                <ChevronRight className="w-4 h-4 shrink-0 text-fg-4" aria-hidden="true" />
            </button>
        </li>
    );
};
